import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';

import { kinds } from '@/app/site/scope';
import { toggleFavorite, useFavorites } from '@/app/site/favorites';
import { useSiteScope } from '@/app/site/useSiteScope';
import { CatalogHead, StarButton } from '@/features/catalog/Catalog';
import catalog from '@/features/catalog/Catalog.module.scss';
import { contentTypesGet, contentTypesUsage } from '@/shared/api/generated/content/content';
import { ServerRulesEditor } from '@/features/rules/ServerRulesEditor';
import { WorkflowEditor } from '@/features/stages/WorkflowEditor';
import { Button } from '@/shared/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/Tabs';

import { CollectionList } from '../ContentPage/ContentPage';
import { RemoveTypeDialog } from '../SchemaPage/RemoveTypeDialog';
import { TypeFields } from '../SchemaPage/TypeFields';
import { TypeSettingsForm } from '../SchemaPage/TypeSettingsForm';
import { usageLine } from '../SchemaPage/usage';

export type ModelTab = 'records' | 'fields' | 'stages' | 'rules' | 'settings';

/**
 * Деталка модели: записи, поля и настройки на одной странице вкладками
 * (решение Давида 20 октября 2026). Вкладка — в адресе: ссылкой можно
 * поделиться, перезагрузка её не теряет.
 */
export function ModelPage() {
  const { siteId, env, typeId } = useParams({ from: '/authed/sites/$siteId/$env/models/$typeId' });
  const { tab = 'records' } = useSearch({ from: '/authed/sites/$siteId/$env/models/$typeId' });
  const navigate = useNavigate();
  const scope = useSiteScope();
  const favorites = useFavorites(siteId);
  const [removing, setRemoving] = useState(false);
  const shaper = scope?.can('site.shape') ?? false;

  const details = useQuery({
    queryKey: ['content-type', typeId],
    queryFn: async () => {
      const response = await contentTypesGet(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос модели');
      }

      return response.data;
    },
  });

  const usage = useQuery({
    queryKey: ['content-type', typeId, 'usage'],
    queryFn: async () => {
      const response = await contentTypesUsage(typeId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос использования');
      }

      return response.data;
    },
  });

  const type = details.data?.type;

  const setTab = (next: string) => {
    void navigate({
      to: '/sites/$siteId/$env/models/$typeId',
      params: { siteId, env, typeId },
      search: next === 'records' ? {} : { tab: next as ModelTab },
      replace: true,
    });
  };

  // Форма живёт в «Формах»: старая ссылка или смена раздела в настройках
  // ведут туда.
  if (type?.purpose === 'users') {
    return <Navigate to="/sites/$siteId/$env/users" params={{ siteId, env }} replace />;
  }

  if (type?.purpose === 'form') {
    return (
      <Navigate to="/sites/$siteId/$env/forms/$typeId" params={{ siteId, env, typeId }} replace />
    );
  }

  if (type === undefined) {
    return details.isError ? (
      <p className={catalog['empty']}>Такой модели нет — возможно, её удалили.</p>
    ) : null;
  }

  return (
    <div className={catalog['page']}>
      <CatalogHead
        title={
          <>
            {type.name}
            <StarButton
              active={favorites.includes(type.slug)}
              onToggle={() => {
                toggleFavorite(siteId, type.slug);
              }}
            />
          </>
        }
        subtitle={
          <>
            <span className={catalog['mono']}>{type.slug}</span>
            {usage.data !== undefined && ` · ${usageLine(type, usage.data)}`}
            {type.description !== '' && ` · ${type.description}`}
          </>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="records" active={tab === 'records'}>
            Записи
          </TabsTrigger>
          <TabsTrigger value="fields" active={tab === 'fields'}>
            Поля
          </TabsTrigger>
          {type.kind === 'collection' && (
            <TabsTrigger value="stages" active={tab === 'stages'}>
              Статусы
            </TabsTrigger>
          )}
          {type.kind === 'collection' && (
            <TabsTrigger value="rules" active={tab === 'rules'}>
              Правила
            </TabsTrigger>
          )}
          {shaper && (
            <TabsTrigger value="settings" active={tab === 'settings'}>
              Настройки
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="records">
          <CollectionList key={`${typeId}:${env}`} typeId={typeId} embedded />
        </TabsContent>

        <TabsContent value="fields">
          <TypeFields type={type} readOnly={!shaper} />
        </TabsContent>

        {type.kind === 'collection' && (
          <TabsContent value="stages">
            <WorkflowEditor key={type.etag} type={type} readOnly={!shaper} />
          </TabsContent>
        )}

        {type.kind === 'collection' && (
          <TabsContent value="rules">
            <ServerRulesEditor key={type.etag} type={type} readOnly={!shaper} />
          </TabsContent>
        )}

        {shaper && (
          <TabsContent value="settings">
            <div className={catalog['page']}>
              <TypeSettingsForm
                key={type.etag}
                siteID={siteId}
                environment={kinds[env]}
                type={type}
                section="model"
                onSaved={() => {
                  void details.refetch();
                }}
              />
              <div>
                <Button
                  variant="ghost"
                  tone="danger"
                  iconLeft={<Trash2 size={15} />}
                  onClick={() => {
                    setRemoving(true);
                  }}
                >
                  Убрать модель в корзину
                </Button>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <RemoveTypeDialog
        type={removing ? type : undefined}
        onClose={() => {
          setRemoving(false);
        }}
        onRemoved={() => {
          void navigate({ to: '/sites/$siteId/$env/models', params: { siteId, env } });
        }}
      />
    </div>
  );
}
